import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, UserRoundX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function deleteInactivateUser(userId: string) {
  await api.backend.delete(`/user/${userId}`)
}

export function useInactivateUser() {
  const { t } = useTranslation('registers')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync } = useMutation<unknown, AxiosError<ApiError>, string>({
    mutationKey: ['/user/id/inactivate'],
    mutationFn: deleteInactivateUser,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: UserRoundX,
          title: t('users.inactivate_user.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/user/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('users.inactivate_user.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('users.inactivate_user.toast_error.description'),
        },
      })
    },
  })

  const handleInactivateUser = (userId: string) => {
    handleModal({
      type: 'warning',
      title: t('users.inactivate_user.modal.title'),
      description: t('users.inactivate_user.modal.description'),
      confirmText: t('users.inactivate_user.modal.button_confirm'),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(userId).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleInactivateUser,
  }
}
